import { useState } from "react";
import { ChevronDown, Check, Loader2 } from "lucide-react";
import { formatPrice } from "@/components/trips/itinerary/itinerary-utils";
import type { PlanFlight, PlanMatrixGroup } from "@/lib/types";
import type { PlanPriceChoice } from "@/lib/fareOptionsTypes";
import { useSelectFocusFlight } from "@/hooks/useSelectFocusFlight";
import AirlineLogo from "./AirlineLogo";
import SourceLed from "./SourceLed";
import FlightDetail from "./FlightDetail";
import PriceHistoryCell from "./PriceHistoryCell";
import JourneyTimeline from "./JourneyTimeline";
import FlightOptionsToolbar from "./FlightOptionsToolbar";
import FareLadder from "./FareLadder";
import ThinSeatsLine from "./ThinSeatsLine";
import { rowPrice, yourPriceLabel } from "./fareLadderView";
import { displayFlightNumber } from "./flightLabel";
import {
  buildFlightRows,
  buildFlightGroups,
  distinctAirlines,
  distinctOrigins,
  DEFAULT_VIEW_STATE,
  type FlightGroup,
  type FlightRow,
  type FlightViewState,
} from "./flightOptionsView";

interface FlightOptionsListProps {
  tripId: string;
  routeId: string;
  matrix: PlanMatrixGroup[];
  originCode?: string | null;
  destinationCode?: string | null;
  /**
   * § G31 — which end of the leg is the TRAVELLER's airport. On a return every
   * option departs the meeting city, so the airport that varies (and the one
   * they care about) is the arrival.
   */
  role?: 'outbound' | 'return' | 'extra' | null;
  defaultOpen?: boolean;
  defaultOpenDetail?: string | null;
  /**
   * Render the list without its own "All flight options" disclosure header.
   * LegFaresPanel supplies the one pulldown for the whole leg; a second header
   * inside it would be a pulldown inside a pulldown.
   */
  bare?: boolean;
  /** What the traveller chose to buy; names what each row's one price includes. */
  priceChoice?: PlanPriceChoice | null;
}

export default function FlightOptionsList({
  tripId,
  routeId,
  matrix,
  originCode,
  destinationCode,
  role,
  defaultOpen = false,
  defaultOpenDetail = null,
  bare = false,
  priceChoice = null,
}: FlightOptionsListProps) {
  // In bare mode the parent panel IS the disclosure, so the list is always open.
  const [open, setOpen] = useState(defaultOpen || bare);
  const [openDetail, setOpenDetail] = useState<string | null>(defaultOpenDetail);
  const [view, setView] = useState<FlightViewState>(DEFAULT_VIEW_STATE);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  const { select, pendingId } = useSelectFocusFlight(tripId, routeId);

  // Date tabs: each matrix group is a date. When a route spans multiple dates
  // (e.g. after changing the date, or with date variants), tab between them so
  // the list never becomes one giant scroll. Default to the focus flight's date.
  const focusDate = matrix.find((g) => g.flights.some((f) => f.chosen))?.date ?? null;
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const multiDate = matrix.length > 1;
  const selectedDate = activeDate ?? focusDate ?? matrix[0]?.date ?? null;

  const totalFlights = matrix.reduce((n, g) => n + g.flights.length, 0);
  if (totalFlights === 0) return null;

  // The flights shown in the body: the selected date only when tabbed, else all.
  const activeMatrix = multiDate ? matrix.filter((g) => g.date === selectedDate) : matrix;

  // Compute once from the full matrix so proportional widths stay stable across filtering/sorting.
  const maxDurationMinutes = Math.max(0, ...matrix.flatMap((g) => g.flights).map((fl) => fl.durationMinutes ?? 0));

  const airlines = distinctAirlines(activeMatrix);
  // § G30 — derived from the FLIGHTS, not the route. A leg filed under a metro
  // code ("NYC") holds JFK, LGA and EWR departures mixed together, and only the
  // segments know which is which.
  const origins = distinctOrigins(activeMatrix, originCode, role);
  const { rows, total, shown } = buildFlightRows(activeMatrix, view, originCode, role);
  // With date tabs the body shows one date at a time, so never re-stack by date.
  const grouped = view.sort === "date" && !multiDate;

  const Row = (r: FlightRow, showDateChip: boolean) => {
    const fl = r.flight;
    const chosen = fl.chosen;
    const busy = pendingId === fl.flightId;
    const detailOpen = openDetail === fl.flightId;
    const num = displayFlightNumber(fl.flightNumber);
    const airlineLabel = (fl.airlineName || fl.airline || "—") + (num ? " " + num : "");
    const yourLabel = yourPriceLabel(fl, priceChoice);
    const aircraft = fl.aircraft && fl.aircraft.trim() ? fl.aircraft.trim() : null;
    return (
      <li key={fl.flightId}>
        <div
          className={
            "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md px-2 py-2 sm:flex-nowrap " +
            (chosen ? "bg-[hsl(var(--primary))]/[0.1]" : "")
          }
        >
          <AirlineLogo iata={fl.airline} logoUrl={fl.logoUrl} size={28} />

          <div className="w-32 shrink-0 text-left">
            <span className="block truncate text-sm font-medium text-foreground">{airlineLabel}</span>
            <span className="block truncate text-[11px] text-muted-foreground">
              {/* § G30 — name the airport when the leg spans more than one.
                  "still I can't tell which airport it is" was the complaint, and
                  for a multi-airport city it is the first thing a traveller
                  needs. Suppressed for a single-airport leg, where repeating the
                  code on every row is noise the header already carries. */}
              {origins.length > 1 && r.origin ? (
                <span className="font-mono font-medium text-foreground">{r.origin}</span>
              ) : null}
              {origins.length > 1 && r.origin ? " · " : ""}
              {aircraft || "Aircraft TBD"}
              {showDateChip ? " · " + r.dateLabel : ""}
            </span>
          </div>

          <div className="order-last w-full min-w-0 sm:order-none sm:flex-1">
            <JourneyTimeline flight={fl} originCode={originCode} destinationCode={destinationCode} maxDurationMinutes={maxDurationMinutes} />
          </div>

          {/* Price-history cell is secondary — hide it on narrower widths so the
              journey timeline has room (it lives in the rail / focus card too). */}
          <div className="hidden w-28 shrink-0 xl:block">
            <PriceHistoryCell history={fl.priceHistory} />
          </div>

          <span className="flex shrink-0 items-center gap-1.5">
            <span className="flex flex-col items-end leading-tight">
              <span className="font-mono tabular-nums text-sm font-semibold text-foreground">{formatPrice(rowPrice(fl)) ?? "—"}</span>
              {yourLabel && (
                <span className="whitespace-nowrap text-[10px] text-muted-foreground sm:hidden">
                  {yourLabel}
                </span>
              )}
            </span>
            <SourceLed source={fl.source} />
            {busy ? (
              <span className="inline-flex items-center gap-0.5 text-xs text-[hsl(var(--primary))]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                setting…
              </span>
            ) : chosen ? (
              <span className="inline-flex items-center gap-0.5 text-xs font-medium text-[hsl(var(--primary))]">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                current focus
              </span>
            ) : (
              <button
                type="button"
                onClick={() => select(fl.flightId)}
                className="rounded border border-[hsl(var(--primary))]/40 px-1.5 py-0.5 text-xs font-medium text-[hsl(var(--accent-foreground))] transition-colors hover:bg-[hsl(var(--primary))]/[0.08]"
              >
                Set as focus
              </button>
            )}
          </span>

          <button
            type="button"
            onClick={() => setOpenDetail(detailOpen ? null : fl.flightId)}
            aria-expanded={detailOpen}
            aria-label={detailOpen ? "Hide flight detail" : "Show flight detail"}
            className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChevronDown className={"h-4 w-4 transition-transform " + (detailOpen ? "rotate-180" : "")} aria-hidden="true" />
          </button>
        </div>
        {chosen && fl.airline === "AA" && (
          <ThinSeatsLine
            tripId={tripId}
            flightNumber={fl.firstLegFlightNumber ?? fl.flightNumber}
            date={matrix.find((g) => g.flights.some((f) => f.flightId === fl.flightId))?.date}
          />
        )}
        <FareLadder flight={fl} priceLabel={yourLabel} tripId={tripId} routeId={routeId} />
        {detailOpen && (
          <div className="px-1 pb-2 pt-1">
            <FlightDetail flight={fl} originCode={originCode ?? null} destinationCode={destinationCode ?? null} />
          </div>
        )}
      </li>
    );
  };

  const GroupBlock = (g: FlightGroup, showDateChip: boolean) => {
    if (g.count === 1) return Row(g.headline, showDateChip);
    const open = openGroups.has(g.key);
    return (
      <li key={g.key}>
        <ul className="space-y-0.5">{[Row(g.headline, showDateChip)]}</ul>
        <button
          type="button"
          onClick={() => toggleGroup(g.key)}
          aria-expanded={open}
          className="ml-10 mt-0.5 flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--primary))] hover:underline"
        >
          <ChevronDown className={"h-3 w-3 transition-transform " + (open ? "rotate-180" : "")} aria-hidden="true" />
          {open ? "Hide connection options" : `${g.count - 1} more via different connections`}
        </button>
        {open && (
          <ul className="ml-6 mt-1 space-y-0.5 border-l border-border/60 pl-2">
            {g.variants.map((v) => Row(v, showDateChip))}
          </ul>
        )}
      </li>
    );
  };

  const groupedRows: { date: string; dateLabel: string; rows: FlightRow[] }[] = [];
  if (grouped) {
    for (const r of rows) {
      const last = groupedRows[groupedRows.length - 1];
      if (last && last.date === r.date) last.rows.push(r);
      else groupedRows.push({ date: r.date, dateLabel: r.dateLabel, rows: [r] });
    }
  }

  return (
    <div
      className={
        bare ? "overflow-hidden" : "mt-3 overflow-hidden rounded-lg border bg-muted/30"
      }
    >
      {!bare && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60"
        >
          <span>All flight options · <span className="font-mono">{totalFlights}</span> {totalFlights === 1 ? "flight" : "flights"}</span>
          <ChevronDown className={"h-4 w-4 transition-transform " + (open ? "rotate-180" : "")} aria-hidden="true" />
        </button>
      )}

      {open && (
        <div className={bare ? "" : "border-t"}>
          {/* Date tabs — one per date, so a long multi-date list stays navigable. */}
          {multiDate && (
            <div className="flex flex-wrap gap-1.5 border-b px-3 py-2">
              {matrix.map((g) => {
                const on = g.date === selectedDate;
                return (
                  <button
                    key={g.date}
                    type="button"
                    onClick={() => setActiveDate(g.date)}
                    aria-pressed={on}
                    className={
                      "rounded-full px-2.5 py-1 text-xs font-medium transition-colors " +
                      (on
                        ? "bg-[hsl(var(--foreground))] text-white"
                        : "border border-border text-muted-foreground hover:bg-muted")
                    }
                  >
                    {g.dateLabel} <span className={on ? "text-white/80" : "text-muted-foreground/70"}>· <span className="font-mono">{g.flights.length}</span></span>
                  </button>
                );
              })}
            </div>
          )}

          <FlightOptionsToolbar
            state={view}
            airlines={airlines}
            origins={origins}
            returnLeg={role === 'return'}
            shown={shown}
            total={total}
            onChange={setView}
          />
          {grouped ? (
            <div className="divide-y divide-border/60">
              {groupedRows.map((g) => (
                <div key={g.date} className="px-2 py-2">
                  <div className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {g.dateLabel}
                  </div>
                  <ul className="space-y-0.5">{buildFlightGroups(g.rows).map((fg) => GroupBlock(fg, false))}</ul>
                </div>
              ))}
            </div>
          ) : (
            <ul className="space-y-0.5 px-2 py-2">{buildFlightGroups(rows).map((fg) => GroupBlock(fg, !multiDate))}</ul>
          )}
        </div>
      )}
    </div>
  );
}
